import { useRef } from "react";

import { useQuery } from "@tanstack/react-query";

import Sentry from "@similarweb/sentry-logger";

import { useSearchAdvertisingDataAndParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useSearchAdvertisingDataAndParams";
import transformData from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/transformData";
import type {
    GraphDataWithZones,
    ITrafficData,
    SelectedKeywords,
} from "app/pages/keyword-research/website-analysis/pages/website-keywords/table/clicks-over-time-graph/types";
import { SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import ColorStack from "app/components/colorsStack/ColorStack";
import { CHART_COLORS } from "app/constants/ChartColors";
import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { hasPartialWeek } from "app/common/keyword-research/keywords-overview/trendOverTimeTimeframeHelpers";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import type { ValueOf } from "pro-features/types";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import type { EKeywordTabs } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { addPartialDataZones } from "app/UtilitiesAndConstants/UtilityFunctions/monthsToDateUtilityFunctions";

import { useWebsiteKeywordsEndpoints } from "../websiteKeywordsEndpoints";

export const useClicksOverTimeData = (
    selectedKeywords?: SelectedKeywords,
    selectedGranularity = GranularityNames.WEEKLY,
    selectedGraphDuration: ValueOf<typeof BasicDurations> = BasicDurations.LAST_THREE_MONTHS,
) => {
    const colorStack = useRef(new ColorStack(CHART_COLORS.chartMainColors));
    const { country, duration, key, selectedPageTab, pageFilter, webSource } = useSearchAdvertisingDataAndParams();
    const endpoints = useWebsiteKeywordsEndpoints();

    const deviceType = webSourceToDeviceMap[webSource];
    const fetchService = DefaultFetchService.getInstance();
    const durationObj = DurationService.getDurationData(duration);
    const {
        forAPI: { from, to, isWindow },
    } = durationObj;

    return useQuery<ITrafficData, unknown, GraphDataWithZones>({
        queryKey: [
            "websiteKeywordsClicksOverTimeTrend",
            key,
            duration,
            webSource,
            selectedKeywords,
            country,
            pageFilter,
            selectedGranularity,
            selectedGraphDuration,
            selectedPageTab,
        ],
        queryFn: async () => {
            let queryParams: URLSearchParams;
            try {
                queryParams = new URLSearchParams({
                    timeGranularity: selectedGranularity,
                    device: deviceType,
                    from,
                    to,
                    keys: key,
                    country: country.toString(),
                    isWindow: isWindow.toString(),
                    sourceType: SourceTypeMap[selectedPageTab as keyof typeof EKeywordTabs],
                    duration: selectedGraphDuration,
                    pageFilterJson: pageFilter,
                    ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
                });

                const response = await fetchService.post<ITrafficData>(
                    `${endpoints.clicksOverTimeTrend}?${queryParams.toString()}`,
                    {
                        keywords: selectedKeywords,
                    },
                );
                return response;
            } catch (error) {
                Sentry.errorTracker({
                    message: "Error fetching clicks over time trend",
                    e: error,
                    level: "error",
                    params: {
                        extra: {
                            queryParams,
                        },
                    },
                });
                throw error;
            }
        },
        select: (responseData: ITrafficData) => {
            try {
                const containsPartialWeek = hasPartialWeek(
                    selectedGranularity,
                    selectedGraphDuration === BasicDurations.LAST_TWENTY_EIGHT_DAYS,
                    durationObj,
                );
                const transformedData = transformData(
                    responseData,
                    colorStack.current,
                    containsPartialWeek,
                ).graphDataItems;
                return addPartialDataZones(transformedData, {
                    chartType: chartTypes.AREA,
                }) as GraphDataWithZones;
            } catch (error) {
                Sentry.errorTracker({
                    message: "Error in data transformation of clicks over time trend",
                    e: error,
                    level: "error",
                });
                throw error;
            }
        },
    });
};
